import React, { createContext, useCallback, useContext, useMemo } from 'react';
import type { Dispatch } from 'react';
import type { ResearchAction, ResearchSection, ResearchState, ResearchWorkspace } from '../types/researchDemo';
import { useDemoStore } from '../utils/demoStorage';
import { createInitialResearchState, researchReducer, researchStorageKey } from '../utils/researchDemo';
type ResearchDemoValue = {
  state: ResearchState;
  dispatch: Dispatch<ResearchAction>;
  reset: () => void;
  persistenceOk: boolean;
  goTo: (workspace: ResearchWorkspace, section: ResearchSection) => void;
  /** Opens a source in the Source Library of the current workspace. */
  inspectSource: (sourceId: string, passageId?: string) => void;
  openClaim: (claimId: string) => void;
};
const ResearchDemoContext = createContext<ResearchDemoValue | null>(null);
export function ResearchDemoProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    state,
    dispatch,
    reset,
    persistenceOk
  } = useDemoStore(researchStorageKey, researchReducer, createInitialResearchState);
  const goTo = useCallback((workspace: ResearchWorkspace, section: ResearchSection) => dispatch({
    type: 'goTo',
    workspace,
    section
  }), [dispatch]);
  const inspectSource = useCallback((sourceId: string, passageId?: string) => {
    dispatch({
      type: 'selectSource',
      id: sourceId,
      passageId: passageId ?? null
    });
    dispatch({
      type: 'setSection',
      section: 'library'
    });
  }, [dispatch]);
  const openClaim = useCallback((claimId: string) => dispatch({
    type: 'openClaim',
    id: claimId
  }), [dispatch]);
  const value = useMemo(() => ({
    state,
    dispatch,
    reset,
    persistenceOk,
    goTo,
    inspectSource,
    openClaim
  }), [state, dispatch, reset, persistenceOk, goTo, inspectSource, openClaim]);
  return <ResearchDemoContext.Provider value={value} data-id="element-2817" data-mp="enkb8z35zyap">{children}</ResearchDemoContext.Provider>;
}
export function useResearchDemo() {
  const value = useContext(ResearchDemoContext);
  if (!value) throw new Error('useResearchDemo must be used within ResearchDemoProvider');
  return value;
}